<template>
  <div>
    <v-container fluid>
      <Govem_bar />
      <base-material-card
        icon="mdi-calculator"
        title="คำนวณค่าตอบแทนพนักงานราชการ"
        class="px-5 py-3"
      >
        <!-- ส่วนการตั้งค่าการประเมิน -->
        <v-row class="mb-4">
          <v-col cols="12">
            <v-alert
              type="error"
              prominent
              outlined
              border="left"
              colored-border
              elevation="2"
              icon="mdi-alert-circle"
            >
              <div class="text-subtitle-1 font-weight-medium">
                อยู่ระหว่างดำเนินการพัฒนา
              </div>
              <div class="text-body-2">
                จะเริ่มใช้งานจริงก็ต่อเมื่อมีประกาศจากส่วนกลาง
              </div>
            </v-alert>
          </v-col>
          <v-col cols="12" md="4">
            <v-select
              v-model="selectedYear"
              :items="years"
              label="ปีงบประมาณ"
              outlined
              dense
              @change="loadEvaluationData"
            ></v-select>
          </v-col>
          <v-col cols="12" md="4">
            <v-text-field
              v-model="compensationPercentage"
              label="เปอร์เซ็นต์การเลื่อนค่าตอบแทน (%)"
              outlined
              dense
              type="number"
              step="0.01"
              suffix="%"
              @input="recalculateBudget"
            ></v-text-field>
          </v-col>
          <v-col cols="12" md="4">
            <v-select
              v-model="selectedHeaders"
              :items="allHeaders"
              label="เลือกคอลัมน์ที่ต้องการแสดง"
              multiple
              outlined
              dense
              return-object
              @change="updateVisibleHeaders"
            >
              <template v-slot:selection="{ item, index }">
                <v-chip v-if="index < 6" small>
                  <span>{{ item.text }}</span>
                </v-chip>
                <span v-if="index === 6" class="grey--text caption"
                  >(+{{ selectedHeaders.length - 6 }} คอลัมน์)</span
                >
              </template>
            </v-select>
          </v-col>
        </v-row>

        <!-- เครื่องมือจัดการ Columns -->
        <v-row class="mb-2">
          <v-col cols="12">
            <v-card outlined class="pa-3">
              <v-row align="center">
                <v-col cols="12" md="8">
                  <div class="text-subtitle-2 mb-2">เครื่องมือจัดการคอลัมน์</div>
                  <v-chip-group multiple column>
                    <v-chip
                      v-for="header in selectedHeaders"
                      :key="header.value"
                      close
                      small
                      color="primary"
                      text-color="white"
                      @click:close="removeHeader(header)"
                    >
                      {{ header.text }}
                    </v-chip>
                  </v-chip-group>
                </v-col>
                <v-col cols="12" md="4" class="text-right">
                  <v-btn
                    small
                    color="success"
                    text
                    @click="selectAllHeaders"
                    class="mr-2"
                  >
                    <v-icon left small>mdi-check-all</v-icon>
                    เลือกทั้งหมด
                  </v-btn>
                  <v-btn
                    small
                    color="info"
                    text
                    @click="selectEssentialHeaders"
                    class="mr-2"
                  >
                    <v-icon left small>mdi-star</v-icon>
                    เฉพาะสำคัญ
                  </v-btn>
                  <v-btn
                    small
                    color="warning"
                    text
                    @click="resetHeaders"
                  >
                    <v-icon left small>mdi-refresh</v-icon>
                    รีเซ็ต
                  </v-btn>
                </v-col>
              </v-row>
            </v-card>
          </v-col>
        </v-row>

        <!-- สรุปวงเงินรวม -->
        <v-row class="mb-4">
          <v-col cols="12">
            <v-card class="pa-4" outlined>
              <v-row>
                <v-col cols="12" md="3">
                  <v-card-text class="text-center">
                    <h3 class="blue--text">{{ totalPersonnel }}</h3>
                    <p>จำนวนพนักงานทั้งหมด</p>
                  </v-card-text>
                </v-col>
                <v-col cols="12" md="3">
                  <v-card-text class="text-center">
                    <h3 class="green--text">
                      {{ formatCurrency(totalSalary) }}
                    </h3>
                    <p>วงเงินเดือนรวม (บาท)</p>
                  </v-card-text>
                </v-col>
                <v-col cols="12" md="3">
                  <v-card-text class="text-center">
                    <h3 class="orange--text">
                      {{ formatCurrency(totalCompensationBudget) }}
                    </h3>
                    <p>วงเงินค่าตอบแทน ({{ compensationPercentage }}%)</p>
                  </v-card-text>
                </v-col>
                <v-col cols="12" md="3">
                  <v-card-text class="text-center">
                    <h3 class="red--text">
                      {{ formatCurrency(totalCompensationDistributed) }}
                    </h3>
                    <p>วงเงินที่จัดสรรแล้ว</p>
                  </v-card-text>
                </v-col>
              </v-row>
            </v-card>
          </v-col>
        </v-row>

        <!-- ตารางผลการคำนวณ -->
        <v-data-table
          :headers="compensationHeaders"
          :items="compensationData"
          :loading="loading"
          class="elevation-1"
          hide-default-footer
          disable-pagination
          fixed-header
          height="600px"
        >
          <template v-slot:item.performance_score="{ item }">
            <v-text-field
              v-model.number="item.performance_score"
              type="number"
              step="0.01"
              dense
              outlined
              @input="calculateCompensation"
              :disabled="calculationComplete"
            ></v-text-field>
          </template>

          <template v-slot:item.salary_s="{ item }">
            <span class="font-weight-bold">{{
              formatCurrency(item.salary_s)
            }}</span>
          </template>

          <template v-slot:item.max_increase_6_percent="{ item }">
            <span class="font-weight-bold purple--text">
              {{ formatCurrency(item.max_increase_6_percent) }}
            </span>
          </template>

          <template v-slot:item.individual_percentage_pre_manage="{ item }">
            <span :class="getPercentageColor(item.individual_percentage_pre_manage)">
              {{ formatPercentage3Decimal(item.individual_percentage_pre_manage) }}
            </span>
          </template>

          <template v-slot:item.initial_increase_pre_manage="{ item }">
            <span class="font-weight-bold teal--text">
              {{ formatCurrency2Decimal(item.initial_increase_pre_manage) }}
            </span>
          </template>

          <template v-slot:item.initial_rounded_pre_manage="{ item }">
            <span class="font-weight-bold indigo--text">
              {{ formatCurrency(item.initial_rounded_pre_manage) }}
            </span>
          </template>

          <template v-slot:item.management_percentage="{ item }">
            <span class="cyan--text">
              {{ formatPercentage3Decimal(item.management_percentage) }}
            </span>
          </template>

          <template v-slot:item.actual_percentage_final="{ item }">
            <span class="green--text font-weight-bold">
              {{ formatPercentage3Decimal(item.actual_percentage_final) }}
            </span>
          </template>

          <template v-slot:item.actual_increase_amount="{ item }">
            <span class="font-weight-bold deep-orange--text">
              {{ formatCurrency2Decimal(item.actual_increase_amount) }}
            </span>
          </template>

          <template v-slot:item.final_rounded="{ item }">
            <span class="font-weight-bold red--text">
              {{ formatCurrency(item.final_rounded) }}
            </span>
          </template>

          <template v-slot:item.actions="{ item }">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  small
                  color="info"
                  v-bind="attrs"
                  v-on="on"
                  @click="viewDetails(item)"
                >
                  <v-icon>mdi-eye</v-icon>
                </v-btn>
              </template>
              <span>ดูรายละเอียด</span>
            </v-tooltip>
          </template>

          <!-- ผลรวมท้ายตาราง -->
          <template v-slot:footer>
            <tr class="font-weight-bold summary-row">
              <td class="text-center">-</td>
              <td class="text-center">รวม</td>
              <td class="text-right">{{ formatCurrency(totalSalary) }}</td>
              <td class="text-center">{{ formatPercentage(totalPerformanceScore) }}</td>
              <td class="text-right">{{ formatCurrency(totalMaxIncrease6Percent) }}</td>
              <td class="text-center">{{ formatPercentage3Decimal(totalIndividualPercentagePreManage) }}</td>
              <td class="text-right">{{ formatCurrency2Decimal(totalInitialIncreasePreManage) }}</td>
              <td class="text-right">{{ formatCurrency(totalInitialRoundedPreManage) }}</td>
              <td class="text-center">{{ formatPercentage3Decimal(totalManagementPercentage) }}</td>
              <td class="text-center">{{ formatPercentage3Decimal(totalActualPercentageFinal) }}</td>
              <td class="text-right">{{ formatCurrency2Decimal(totalActualIncreaseAmount) }}</td>
              <td class="text-right">{{ formatCurrency(totalFinalRounded) }}</td>
              <td class="text-center">-</td>
            </tr>
          </template>
        </v-data-table>

        <!-- ปุ่มดำเนินการ -->
        <v-row class="mt-4">
          <v-col cols="12" class="text-center">
            <v-btn
              color="primary"
              large
              @click="calculateCompensation"
              :disabled="loading || !canCalculate"
            >
              <v-icon left>mdi-calculator</v-icon>
              คำนวณค่าตอบแทน
            </v-btn>

            <v-btn
              color="success"
              large
              class="ml-2"
              @click="approveCompensation"
              :disabled="!canApprove"
            >
              <v-icon left>mdi-check</v-icon>
              อนุมัติค่าตอบแทน
            </v-btn>

            <v-btn
              color="info"
              large
              class="ml-2"
              @click="exportReport"
              :disabled="compensationData.length === 0"
            >
              <v-icon left>mdi-file-excel</v-icon>
              ส่งออกรายงาน
            </v-btn>
          </v-col>
        </v-row>
      </base-material-card>

      <!-- Dialog แสดงรายละเอียด -->
      <v-dialog v-model="detailDialog" max-width="1200px">
        <v-card>
          <v-card-title>
            <span class="headline">รายละเอียดการคำนวณค่าตอบแทน</span>
          </v-card-title>
          <v-card-text>
            <v-container v-if="selectedEmployee">
              <v-row>
                <v-col cols="12" md="6">
                  <v-text-field
                    label="ชื่อ-นามสกุล"
                    :value="
                      selectedEmployee.frist_name +
                        ' ' +
                        selectedEmployee.last_name
                    "
                    readonly
                    outlined
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    label="ตำแหน่ง"
                    :value="selectedEmployee.name_postion"
                    readonly
                    outlined
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    label="เงินเดือนปัจจุบัน"
                    :value="formatCurrency(selectedEmployee.salary_s)"
                    readonly
                    outlined
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    label="คะแนนการปฏิบัติงาน"
                    :value="selectedEmployee.performance_score"
                    readonly
                    outlined
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    label="เลื่อนได้ไม่เกินร้อยละ 6"
                    :value="formatCurrency(selectedEmployee.max_increase_6_percent)"
                    readonly
                    outlined
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    label="ร้อยละที่ใช้เลื่อนรายบุคคล (ยังไม่บริหาร)"
                    :value="
                      formatPercentage(selectedEmployee.individual_percentage_pre_manage) +
                        '%'
                    "
                    readonly
                    outlined
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    label="เงินเลื่อนขั้นต้น (เลื่อน)"
                    :value="formatCurrency(selectedEmployee.initial_increase_pre_manage)"
                    readonly
                    outlined
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    label="ปัดเศษสิบ"
                    :value="formatCurrency(selectedEmployee.initial_rounded_pre_manage)"
                    readonly
                    outlined
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    label="ร้อยละเลื่อน (บริหารวงเงิน)"
                    :value="
                      formatPercentage(selectedEmployee.management_percentage) +
                        '%'
                    "
                    readonly
                    outlined
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    label="ร้อยละเลื่อนจริง"
                    :value="
                      formatPercentage(selectedEmployee.actual_percentage_final) + '%'
                    "
                    readonly
                    outlined
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    label="จำนวนเงินที่ใช้เลื่อน"
                    :value="formatCurrency(selectedEmployee.actual_increase_amount)"
                    readonly
                    outlined
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    label="เลื่อนจริงปัดเศษสิบ"
                    :value="formatCurrency(selectedEmployee.final_rounded)"
                    readonly
                    outlined
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    label="เงินเดือนใหม่"
                    :value="formatCurrency(selectedEmployee.new_salary)"
                    readonly
                    outlined
                  ></v-text-field>
                </v-col>
              </v-row>
            </v-container>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="blue darken-1" text @click="detailDialog = false">
              ปิด
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Snackbar แสดงผลลัพธ์ -->
      <v-snackbar
        v-model="snackbar.show"
        :color="snackbar.color"
        :timeout="snackbar.timeout"
        top
      >
        <v-icon left>{{ snackbar.icon }}</v-icon>
        {{ snackbar.text }}
        <template v-slot:action="{ attrs }">
          <v-btn text v-bind="attrs" @click="snackbar.show = false">
            ปิด
          </v-btn>
        </template>
      </v-snackbar>
    </v-container>
  </div>
</template>

<script>
import Govem_bar from "../../../components/college/govem_bar.vue";

export default {
  name: "CalculateCompensation",
  components: {
    Govem_bar
  },
  data() {
    return {
      ApiKey: "HRvec2021",
      loading: false,
      selectedYear: new Date().getFullYear() + 543, // ปี พ.ศ.
      compensationPercentage: 4.0,
      calculationComplete: false,
      detailDialog: false,
      selectedEmployee: null,

      // ข้อมูลพนักงาน
      personnel_temporarys: [],
      compensationData: [],

      // สถิติรวม
      totalPersonnel: 0,
      totalSalary: 0,
      totalCompensationBudget: 0,
      totalCompensationDistributed: 0,
      totalInitialRounded: 0,
      remainingBudget: 0,

      // ตัวเลือกปี
      years: [],

      // ตัวแปรสำหรับจัดการ columns
      selectedHeaders: [],
      visibleHeaders: [],

      // Headers สำหรับตาราง (ปรับตามภาพที่แนบ - 12 columns หลัก)
      allHeaders: [
        { text: "ลำดับที่", value: "order", width: "80px", align: "center", essential: true },
        { text: "ชื่อ-สกุล", value: "full_name", width: "150px", essential: true },
        { text: "ค่าตอบแทน", value: "salary_s", width: "120px", align: "right", essential: true },
        {
          text: "คะแนนผลการปฏิบัติงานเฉลี่ย",
          value: "performance_score",
          width: "120px",
          align: "center",
          essential: true
        },
        {
          text: "เลื่อนได้ไม่เกิน 6",
          value: "max_increase_6_percent",
          width: "120px",
          align: "right",
          essential: true
        },
        {
          text: "ร้อยละที่ได้เลื่อนรายบุคคล (ยังไม่บริหารวงเงิน)",
          value: "individual_percentage_pre_manage",
          width: "160px",
          align: "center",
          essential: true
        },
        {
          text: "ใช้เงินเลื่อนขั้นต้น เลื่อน",
          value: "initial_increase_pre_manage",
          width: "140px",
          align: "right",
          essential: true
        },
        {
          text: "ปัดเศษสิบ",
          value: "initial_rounded_pre_manage",
          width: "100px",
          align: "right",
          essential: true
        },
        {
          text: "บวก ร้อยละเลื่อน (บริหารวงเงิน)",
          value: "management_percentage",
          width: "140px",
          align: "center",
          essential: true
        },
        {
          text: "ร้อยละเลื่อนจริง",
          value: "actual_percentage_final",
          width: "120px",
          align: "center",
          essential: true
        },
        {
          text: "เลื่อนจริง เลื่อน",
          value: "actual_increase_amount",
          width: "120px",
          align: "right",
          essential: true
        },
        {
          text: "ปัดเศษสิบ",
          value: "final_rounded",
          width: "100px",
          align: "right",
          essential: true
        },
        {
          text: "ดำเนินการ",
          value: "actions",
          width: "100px",
          align: "center",
          sortable: false,
          essential: false
        }
      ],

      // Snackbar
      snackbar: {
        show: false,
        color: "success",
        timeout: 3000,
        icon: "mdi-check",
        text: ""
      },

      user: {}
    };
  },

  computed: {
    // Dynamic computed property for headers
    compensationHeaders() {
      return this.visibleHeaders;
    },
    canCalculate() {
      return (
        this.compensationData.length > 0 &&
        this.compensationData.every(item => item.performance_score > 0)
      );
    },

    canApprove() {
      return (
        this.canCalculate &&
        this.totalCompensationDistributed > 0 &&
        this.totalCompensationDistributed <= this.totalCompensationBudget
      ); // ต้องไม่เกินวงเงินค่าตอบแทน
    },

    // คำนวณผลรวมแต่ละคอลัมน์ (เพิ่มคอลัมน์ใหม่)
    totalPerformanceScore() {
      return this.compensationData.reduce(
        (sum, item) => sum + (item.performance_score || 0),
        0
      );
    },

    totalMaxIncrease6Percent() {
      return this.compensationData.reduce(
        (sum, item) => sum + (item.max_increase_6_percent || 0),
        0
      );
    },

    totalIndividualPercentagePreManage() {
      return this.compensationData.reduce(
        (sum, item) => sum + (item.individual_percentage_pre_manage || 0),
        0
      );
    },

    // New computed properties for new columns
    totalInitialIncreasePreManage() {
      return this.compensationData.reduce(
        (sum, item) => sum + (item.initial_increase_pre_manage || 0),
        0
      );
    },

    totalInitialRoundedPreManage() {
      return this.compensationData.reduce(
        (sum, item) => sum + (item.initial_rounded_pre_manage || 0),
        0
      );
    },

    totalManagementPercentage() {
      return this.compensationData.reduce(
        (sum, item) => sum + (item.management_percentage || 0),
        0
      );
    },

    totalActualPercentageFinal() {
      return this.compensationData.reduce(
        (sum, item) => sum + (item.actual_percentage_final || 0),
        0
      );
    },

    totalActualIncreaseAmount() {
      return this.compensationData.reduce(
        (sum, item) => sum + (item.actual_increase_amount || 0),
        0
      );
    },

    totalIndividualPercentage() {
      return this.compensationData.reduce(
        (sum, item) => sum + (item.individual_percentage || 0),
        0
      );
    },

    totalInitialIncrease() {
      return this.compensationData.reduce(
        (sum, item) => sum + (item.initial_increase || 0),
        0
      );
    },

    totalInitialRoundedSum() {
      return this.compensationData.reduce(
        (sum, item) => sum + (item.initial_rounded || 0),
        0
      );
    },

    totalManagedPercentage() {
      return this.compensationData.reduce(
        (sum, item) => sum + (item.managed_percentage || 0),
        0
      );
    },

    totalActualPercentage() {
      return this.compensationData.reduce(
        (sum, item) => sum + (item.actual_percentage || 0),
        0
      );
    },

    totalActualIncrease() {
      return this.compensationData.reduce(
        (sum, item) => sum + (item.actual_increase || 0),
        0
      );
    },

    totalFinalRounded() {
      return this.compensationData.reduce(
        (sum, item) => sum + (item.final_rounded || 0),
        0
      );
    },

    totalNewSalary() {
      return this.compensationData.reduce(
        (sum, item) => sum + (item.new_salary || 0),
        0
      );
    }
  },

  watch: {
    compensationPercentage: {
      handler(newVal) {
        if (newVal && this.compensationData.length > 0) {
          this.recalculateBudget();
        }
      }
    }
  },

  async mounted() {
    await this.initializeComponent();
    this.initializeHeaders();
  },

  methods: {
    initializeHeaders() {
      // เลือก essential columns เป็นค่าเริ่มต้น
      this.selectedHeaders = this.allHeaders.filter(header => header.essential);
      this.visibleHeaders = [...this.selectedHeaders];
    },

    updateVisibleHeaders() {
      // อัปเดต visibleHeaders ตามที่เลือก
      this.visibleHeaders = [...this.selectedHeaders];
    },

    resetHeaders() {
      // รีเซ็ตกลับไปเป็นค่าเริ่มต้น
      this.initializeHeaders();
    },

    selectAllHeaders() {
      // เลือกทุก columns
      this.selectedHeaders = [...this.allHeaders];
      this.updateVisibleHeaders();
    },

    selectEssentialHeaders() {
      // เลือกเฉพาะ essential columns
      this.selectedHeaders = this.allHeaders.filter(header => header.essential);
      this.updateVisibleHeaders();
    },
    async initializeComponent() {
      this.loading = true;
      try {
        // โหลดข้อมูล user session
        let userSession = JSON.parse(sessionStorage.getItem("user")) || {};

        let resultUser = await this.$http.post("user.php", {
          ApiKey: this.ApiKey,
          user_ID: userSession.user_ID
        });
        this.user = resultUser.data;

        // สร้างรายการปี (5 ปีย้อนหลัง)
        this.generateYears();

        // โหลดข้อมูลพนักงาน
        await this.loadEvaluationData();
        if (!this.compensationData.length) {
          await this.loadPersonnelData();
        }
      } catch (error) {
        this.showSnackbar(
          "เกิดข้อผิดพลาดในการโหลดข้อมูล",
          "error",
          "mdi-alert"
        );
      } finally {
        this.loading = false;
      }
    },

    generateYears() {
      const currentYear = new Date().getFullYear() + 543;
      this.years = [];
      for (let i = 0; i < 5; i++) {
        this.years.push(currentYear - i);
      }
    },

    async loadPersonnelData() {
      try {
        let result = await this.$http.post("personnel_temporary_govem.php", {
          ApiKey: this.ApiKey,
          user_code: this.user.user_code,
          user_status: "gov_em"
        });

        this.personnel_temporarys = result.data;
        this.prepareCompensationData();
        this.calculateTotalSalary();
      } catch (error) {
        this.showSnackbar(
          "ไม่สามารถโหลดข้อมูลพนักงานได้",
          "error",
          "mdi-alert"
        );
      }
    },

    prepareCompensationData() {
      this.compensationData = this.personnel_temporarys.map(
        (employee, index) => ({
          order: index + 1,
          id_card: employee.id_card,
          frist_name: employee.frist_name,
          last_name: employee.last_name,
          full_name: `${employee.frist_name} ${employee.last_name}`,
          name_postion: employee.name_postion,
          salary_s: parseFloat(employee.salary_s) || 0,
          performance_score: 0, // จะต้องกรอกโดย user
          max_increase_6_percent: 0, // เลื่อนได้ไม่เกิน 6% = เงินเดือน * 0.06
          individual_percentage_pre_manage: 0, // ร้อยละที่ใช้เลื่อนรายบุคคล (ยังไม่บริหารวงเงิน)
          
          // New fields
          initial_increase_pre_manage: 0, // เงินเลื่อนขั้นต้น (เลื่อน) = เงินเดือน * ร้อยละที่ใช้เลื่อนรายบุคคล(ยังไม่บริหารวงเงิน) / 100
          initial_rounded_pre_manage: 0, // ปัดเศษสิบ
          management_percentage: 0, // ร้อยละเลื่อน (บริหารวงเงิน) - เฉลี่ยจากวงเงินที่เหลือ
          actual_percentage_final: 0, // ร้อยละเลื่อนจริง = individual_percentage_pre_manage + management_percentage
          actual_increase_amount: 0, // จำนวนเงินที่ใช้เลื่อน = เงินเดือน * actual_percentage_final / 100
          
          individual_percentage: 0, // ร้อยละเลื่อนรายบุคคล (ยังไม่บริหารวงเงิน)
          initial_increase: 0, // เงินเลื่อนขั้นต้น
          initial_rounded: 0, // ปัดเศษขั้นต้น (หลักสิบ)
          managed_percentage: 0, // ร้อยละบริหารวงเงิน
          actual_percentage: 0, // ร้อยละเลื่อนจริง
          actual_increase: 0, // เลื่อนจริง
          final_rounded: 0, // ปัดเศษจริง (หลักสิบ)
          new_salary: parseFloat(employee.salary_s) || 0
        })
      );
    },

    calculateTotalSalary() {
      this.totalPersonnel = this.compensationData.length;
      this.totalSalary = this.compensationData.reduce(
        (sum, item) => sum + item.salary_s,
        0
      );
      this.totalCompensationBudget =
        this.totalSalary * (this.compensationPercentage / 100);
    },

    updateStatistics() {
      // อัปเดตสถิติหลังจากคำนวณ
      this.totalInitialRounded = this.compensationData.reduce(
        (sum, item) => sum + item.initial_rounded,
        0
      );
      this.remainingBudget =
        this.totalCompensationBudget - this.totalInitialRounded;
      this.totalCompensationDistributed = this.compensationData.reduce(
        (sum, item) => sum + item.final_rounded,
        0
      );
    },

    recalculateBudget() {
      // คำนวณวงเงินใหม่เมื่อเปลี่ยนเปอร์เซ็นต์
      this.calculateTotalSalary();
      // รีเซ็ตการคำนวณที่มีอยู่
      this.resetCalculation();
    },

    resetCalculation() {
      this.compensationData.forEach(item => {
        item.max_increase_6_percent = 0;
        item.individual_percentage_pre_manage = 0;
        
        // Reset new fields
        item.initial_increase_pre_manage = 0;
        item.initial_rounded_pre_manage = 0;
        item.management_percentage = 0;
        item.actual_percentage_final = 0;
        item.actual_increase_amount = 0;
        
        item.individual_percentage = 0;
        item.initial_increase = 0;
        item.initial_rounded = 0;
        item.managed_percentage = 0;
        item.actual_percentage = 0;
        item.actual_increase = 0;
        item.final_rounded = 0;
        item.new_salary = item.salary_s;
      });
      this.totalCompensationDistributed = 0;
      this.totalInitialRounded = 0;
      this.remainingBudget = 0;
      this.calculationComplete = false;
    },

    calculateCompensation() {
      if (!this.canCalculate) {
        this.showSnackbar(
          "กรุณากรอกคะแนนการปฏิบัติงานให้ครบทุกคน",
          "warning",
          "mdi-alert"
        );
        return;
      }

      // คำนวณผลรวมคะแนน
      const totalScore = this.compensationData.reduce(
        (sum, item) => sum + item.performance_score,
        0
      );

      if (totalScore === 0) {
        this.showSnackbar(
          "ผลรวมคะแนนไม่สามารถเป็น 0 ได้",
          "error",
          "mdi-alert"
        );
        return;
      }

      // ขั้นตอนที่ 1: คำนวณค่าใหม่ที่เพิ่มเข้ามา
      this.compensationData.forEach(item => {
        // เลื่อนได้ไม่เกิน 6% = เงินเดือน × 0.06
        item.max_increase_6_percent = item.salary_s * 0.06;

        // ร้อยละที่ใช้เลื่อนรายบุคคล (ยังไม่บริหารวงเงิน) = 6 × คะแนนส่วนบุคคล / คะแนนรวม (ทศนิยม 3 หลัก)
        item.individual_percentage_pre_manage = parseFloat(((6 * item.performance_score) / totalScore).toFixed(3));

        // NEW: ใช้เงินเลื่อนขั้นต้น เลื่อน = ค่าตอบแทน × ร้อยละที่ได้เลื่อนรายบุคคล(ทศนิยม 3 หลัก) / 100 (แสดงทศนิยม 2 หลัก)
        item.initial_increase_pre_manage = parseFloat((item.salary_s * (item.individual_percentage_pre_manage / 100)).toFixed(2));

        // NEW: ปัดเศษสิบ
        item.initial_rounded_pre_manage = Math.round(item.initial_increase_pre_manage / 10) * 10;

        // ร้อยละเลื่อนรายบุคคล = (คะแนน × วงเงินค่าตอบแทน) / (ผลรวมคะแนน × เงินเดือน) × 100
        item.individual_percentage =
          ((item.performance_score * this.totalCompensationBudget) /
            (totalScore * item.salary_s)) *
          100;

        // จำกัดไม่เกินร้อยละ 6
        if (item.individual_percentage > 6) {
          item.individual_percentage = 6;
        }

        // เงินเลื่อนขั้นต้น = เงินเดือน × (ร้อยละเลื่อนรายบุคคล / 100)
        item.initial_increase =
          item.salary_s * (item.individual_percentage / 100);

        // ปัดเศษเป็นหลักสิบ
        item.initial_rounded = Math.round(item.initial_increase / 10) * 10;
      });

      // ขั้นตอนที่ 2: คำนวณวงเงินที่ใช้ไปจากการปัดเศษใหม่
      const totalInitialRoundedPreManage = this.compensationData.reduce(
        (sum, item) => sum + item.initial_rounded_pre_manage,
        0
      );
      const remainingBudgetNew = this.totalCompensationBudget - totalInitialRoundedPreManage;

      // ขั้นตอนที่ 3: คำนวณร้อยละบริหารวงเงิน (เฉลี่ยเท่ากันทุกราย) ทศนิยม 3 หลัก
      const managementPercentagePerPerson = parseFloat(((remainingBudgetNew / this.totalSalary) * 100).toFixed(3));

      // ขั้นตอนที่ 4: คำนวณค่าใหม่สำหรับแต่ละคน
      this.compensationData.forEach(item => {
        // NEW: ร้อยละเลื่อน (บริหารวงเงิน) - เฉลี่ยเท่ากันทุกราย ทศนิยม 3 หลัก
        item.management_percentage = managementPercentagePerPerson;

        // NEW: ร้อยละเลื่อนจริง = ร้อยละที่ใช้เลื่อนรายบุคคล(ทศนิยม 3 หลัก) + ร้อยละเลื่อน(บริหารวงเงิน ทศนิยม 3 หลัก)
        item.actual_percentage_final = parseFloat((item.individual_percentage_pre_manage + item.management_percentage).toFixed(3));

        // NEW: จำนวนเงินที่ใช้เลื่อน = ค่าตอบแทน × ร้อยละเลื่อนจริง(ทศนิยม 3 หลัก) / 100 (แสดงทศนิยม 2 หลัก)
        item.actual_increase_amount = parseFloat((item.salary_s * (item.actual_percentage_final / 100)).toFixed(2));
      });

      // ขั้นตอนที่ 5: การปัดเศษสิบ (ไม่ให้เกินวงเงินค่าตอบแทน)
      this.roundToTenWithBudgetControl();

      // อัปเดตสถิติ
      this.updateStatistics();

      this.showSnackbar("คำนวณค่าตอบแทนเรียบร้อยแล้ว", "success", "mdi-check");
    },

    roundToTenWithBudgetControl() {
      // ขั้นที่ 1: ปัดเศษสิบปกติทุกรายการ
      this.compensationData.forEach(item => {
        item.final_rounded = Math.round(item.actual_increase_amount / 10) * 10;
      });

      // ขั้นที่ 2: ตรวจสอบผลรวม
      let totalRounded = this.compensationData.reduce((sum, item) => sum + item.final_rounded, 0);

      // ขั้นที่ 3: หากเกินวงเงิน ลดทีละ 10 บาทจากรายที่มากที่สุด
      while (totalRounded > this.totalCompensationBudget) {
        // หารายที่มีจำนวนมากที่สุดและลดได้
        let maxItem = this.compensationData
          .filter(item => item.final_rounded >= 10)
          .reduce((max, item) => item.final_rounded > max.final_rounded ? item : max, { final_rounded: -1 });

        if (maxItem.final_rounded === -1) break; // ไม่สามารถลดได้แล้ว

        maxItem.final_rounded -= 10;
        totalRounded -= 10;
      }

      // ขั้นที่ 4: หากน้อยกว่าวงเงิน เพิ่มทีละ 10 บาทให้รายที่น้อยที่สุด
      while (totalRounded < this.totalCompensationBudget - 10) { // เหลือพอที่จะเพิ่ม 10 บาท
        // หารายที่มีจำนวนน้อยที่สุด
        let minItem = this.compensationData
          .reduce((min, item) => item.final_rounded < min.final_rounded ? item : min, this.compensationData[0]);

        minItem.final_rounded += 10;
        totalRounded += 10;
      }

        // คำนวณ old logic ด้วย (ลบออก เนื่องจากใช้ logic ใหม่แทน)
        // const totalInitialRounded = this.compensationData.reduce(
        //   (sum, item) => sum + item.initial_rounded,
        //   0
        // );
        // const remainingBudget =
        //   this.totalCompensationBudget - totalInitialRounded;
        // const managedPercentage = (remainingBudget / this.totalSalary) * 100;

        // // ร้อยละบริหารวงเงิน
        // item.managed_percentage = managedPercentage;

        // // ร้อยละเลื่อนจริง = ร้อยละเลื่อนรายบุคคล + ร้อยละบริหารวงเงิน
        // item.actual_percentage =
        //   item.individual_percentage + item.managed_percentage;

        // // เลื่อนจริง = เงินเดือน × (ร้อยละเลื่อนจริง / 100)
        // item.actual_increase = item.salary_s * (item.actual_percentage / 100);

        // // ปัดเศษเป็นหลักสิบ (ไม่มีทศนิยม)
        // item.final_rounded = Math.round(item.actual_increase / 10) * 10;

        // // เงินเดือนใหม่ = เงินเดือนเดิม + เงินที่เลื่อน (ปัดเศษแล้ว)
        // item.new_salary = item.salary_s + item.final_rounded;
      //});

      // อัปเดตสถิติ
      this.updateStatistics();

      this.showSnackbar("คำนวณค่าตอบแทนเรียบร้อยแล้ว", "success", "mdi-check");
    },

    async approveCompensation() {
      if (!this.canApprove) {
        this.showSnackbar(
          "ไม่สามารถอนุมัติได้ กรุณาตรวจสอบการคำนวณ",
          "error",
          "mdi-alert"
        );
        return;
      }

      try {
        this.loading = true;

        // ส่งข้อมูลไปบันทึกในฐานข้อมูล
        let result = await this.$http.post("compensation_approve.php", {
          ApiKey: this.ApiKey,
          year: this.selectedYear,
          college_code: this.user.user_code,
          compensation_data: this.compensationData,
          total_budget: this.totalCompensationBudget,
          total_distributed: this.totalCompensationDistributed,
          percentage: this.compensationPercentage
        });

        if (result.data.status) {
          this.calculationComplete = true;
          this.showSnackbar(
            "อนุมัติค่าตอบแทนเรียบร้อยแล้ว",
            "success",
            "mdi-check"
          );
        } else {
          this.showSnackbar("เกิดข้อผิดพลาดในการอนุมัติ", "error", "mdi-alert");
        }
      } catch (error) {
        this.showSnackbar("เกิดข้อผิดพลาดในการอนุมัติ", "error", "mdi-alert");
      } finally {
        this.loading = false;
      }
    },

    async exportReport() {
      try {
        // ส่งข้อมูลไปสร้างรายงาน Excel
        let result = await this.$http.post("compensation_export.php", {
          ApiKey: this.ApiKey,
          year: this.selectedYear,
          college_code: this.user.user_code,
          compensation_data: this.compensationData,
          summary: {
            totalPersonnel: this.totalPersonnel,
            totalSalary: this.totalSalary,
            totalCompensationBudget: this.totalCompensationBudget,
            totalCompensationDistributed: this.totalCompensationDistributed,
            percentage: this.compensationPercentage
          }
        });
        
        if (result.data.file_url) {
          // เปิดไฟล์ใน tab ใหม่
          window.open(result.data.file_url, "_blank");
          this.showSnackbar(
            "ส่งออกรายงานเรียบร้อยแล้ว",
            "success",
            "mdi-check"
          );
        }
      } catch (error) {
        this.showSnackbar(
          "เกิดข้อผิดพลาดในการส่งออกรายงาน",
          "error",
          "mdi-alert"
        );
      }
    },

    async loadEvaluationData() {
      // โหลดข้อมูลการประเมินที่มีอยู่แล้วในปีที่เลือก
      try {
        let result = await this.$http.post("compensation_history.php", {
          ApiKey: this.ApiKey,
          year: this.selectedYear,
          college_code: this.user.user_code
        });
        
        if (result.data.length > 0) {
          // มีข้อมูลการประเมินแล้ว ให้แสดงข้อมูลเดิม
          this.compensationData = result.data;
          this.calculationComplete = true;
          this.updateStatistics();
        }
      } catch (error) {
        console.log("ไม่มีข้อมูลการประเมินในปีนี้");
      }
    },

    viewDetails(item) {
      this.selectedEmployee = item;
      this.detailDialog = true;
    },

    getPercentageColor(percentage) {
      if (percentage >= 5) return "success--text";
      if (percentage >= 3) return "info--text";
      if (percentage > 0) return "warning--text";
      return "error--text";
    },

    formatCurrency(value) {
      if (isNaN(value) || value === null || value === undefined) return "0";
      return Number(value).toLocaleString("th-TH", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      });
    },

    formatPercentage(value) {
      if (isNaN(value) || value === null || value === undefined) return "0.00";
      return Number(value).toFixed(2);
    },

    formatPercentage3Decimal(value) {
      if (isNaN(value) || value === null || value === undefined) return "0.000";
      return Number(value).toFixed(3);
    },

    formatCurrency2Decimal(value) {
      if (isNaN(value) || value === null || value === undefined) return "0.00";
      return Number(value).toLocaleString("th-TH", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
    },


      resetAll() {
      if (confirm("ต้องการเริ่มต้นใหม่? ข้อมูลการคำนวณปัจจุบันจะหายไป")) {
        this.calculationComplete = false;
        this.resetCalculation();
        this.loadPersonnelData();
        this.showSnackbar("เริ่มต้นใหม่เรียบร้อย", "info", "mdi-information");
      }
    },

    removeHeader(headerToRemove) {
      // ลบ header ที่ไม่ต้องการ (ยกเว้น essential headers)
      if (!headerToRemove.essential) {
        this.selectedHeaders = this.selectedHeaders.filter(
          header => header.value !== headerToRemove.value
        );
        this.updateVisibleHeaders();
      } else {
        this.showSnackbar(
          "ไม่สามารถลบคอลัมน์ที่จำเป็นได้",
          "warning",
          "mdi-alert"
        );
      }
    },
    

    showSnackbar(text, color = "success", icon = "mdi-check") {
      this.snackbar = {
        show: true,
        text,
        color,
        icon,
        timeout: 3000
      };
    }
  }
};
</script>

<style scoped>
.v-data-table >>> .v-data-table__wrapper {
  overflow-x: auto;
}

.v-card-text h3 {
  font-size: 1.5rem;
  font-weight: bold;
}

.v-card-text p {
  margin-bottom: 0;
  font-size: 0.875rem;
}

.headline {
  font-weight: 500;
}

.v-text-field >>> .v-input__control {
  min-height: 40px;
}

.elevation-1 {
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

/* สีสำหรับแยกแยะข้อมูลในตาราง */
.success--text {
  color: #4caf50 !important;
}

.info--text {
  color: #2196f3 !important;
}

.warning--text {
  color: #ff9800 !important;
}

.error--text {
  color: #f44336 !important;
}

.primary--text {
  color: #1976d2 !important;
}

.orange--text {
  color: #ff5722 !important;
}

.green--text {
  color: #388e3c !important;
}

.red--text {
  color: #d32f2f !important;
}

.purple--text {
  color: #7b1fa2 !important;
}

.blue--text {
  color: #1565c0 !important;
}

.teal--text {
  color: #00796b !important;
}

.indigo--text {
  color: #3f51b5 !important;
}

.cyan--text {
  color: #00bcd4 !important;
}

.deep-orange--text {
  color: #ff5722 !important;
}

/* Style สำหรับแถวผลรวม */
.summary-row {
  background-color: #f5f5f5 !important;
  border-top: 2px solid #ddd !important;
}

.summary-row td {
  font-weight: bold !important;
  color: #333 !important;
}

.v-data-table >>> .v-data-footer {
  border-top: 2px solid #ddd;
}

/* สำหรับตารางที่มีความกว้างมาก */
.v-data-table >>> .v-data-table__wrapper {
  max-height: 600px;
  overflow-y: auto;
}

/* สำหรับ chip group */
.v-chip-group {
  max-height: 120px;
  overflow-y: auto;
}

/* การ์ดเครื่องมือ */
.v-card.outlined {
  border: 1px solid #e0e0e0;
}

.text-subtitle-2 {
  font-weight: 600;
  color: #424242;
}

/* ปุ่มเครื่องมือ */
.v-btn.v-size--small {
  font-size: 0.75rem;
}

/* Chips ใน selection */
.v-select__selections .v-chip {
  margin: 2px 4px 2px 0;
}

/* ปรับแต่งความสูงของ select */
.v-input--dense .v-input__control {
  min-height: 40px;
}
</style>